import { useEffect, useState } from "react";
import { Link } from "react-router";
import { AttenzioButton } from "@/components/design-system";
import {
  type CookieConsentRecord,
  readCookieConsent,
  writeCookieConsent,
} from "@/lib/cookie-consent";

function startSentry() {
  void import("@/lib/sentry").then((m) => m.initSentry());
}

/**
 * Non-blocking privacy banner. Essential session cookies (auth) are unaffected.
 * Analytics / Sentry Replay only start after Accept.
 */
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const existing = readCookieConsent();
    if (!existing) {
      setVisible(true);
      return;
    }
    if (existing.choice === "accepted") {
      startSentry();
    }
  }, []);

  useEffect(() => {
    const onConsent = (event: Event) => {
      const detail = (event as CustomEvent<CookieConsentRecord>).detail;
      if (detail?.choice === "accepted") startSentry();
    };
    window.addEventListener("attenzio:cookie-consent", onConsent);
    return () => window.removeEventListener("attenzio:cookie-consent", onConsent);
  }, []);

  if (!visible) return null;

  const choose = (choice: "accepted" | "rejected") => {
    writeCookieConsent(choice);
    if (choice === "accepted") startSentry();
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[100] p-4 sm:p-6 pointer-events-none"
    >
      <div
        className="pointer-events-auto mx-auto max-w-[720px] rounded-[var(--radius-lg)] border px-5 py-4 sm:px-6 sm:py-5 shadow-lg"
        style={{
          background: "var(--surface-card)",
          borderColor: "var(--border-default)",
          boxShadow: "0 -8px 32px rgba(11, 15, 20, 0.12)",
        }}
      >
        <p
          className="uppercase tracking-[0.08em] mb-2"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "11px",
            color: "var(--blue-600)",
          }}
        >
          Privacy
        </p>
        <p
          className="text-[15px] font-semibold leading-snug mb-1.5"
          style={{ fontFamily: "var(--font-display)", color: "var(--text-strong)" }}
        >
          Cookies &amp; optional analytics
        </p>
        <p className="text-[13px] leading-relaxed mb-4" style={{ color: "var(--text-muted)" }}>
          We use essential cookies so you can sign in and stay logged in. Optional analytics
          (error monitoring and session diagnostics) help us improve the product — they stay off
          until you accept.{" "}
          <Link
            to="/privacy-policy"
            className="underline underline-offset-2"
            style={{ color: "var(--brand)" }}
          >
            Privacy Policy
          </Link>
        </p>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <AttenzioButton variant="ghost" size="sm" onClick={() => choose("rejected")}>
            Reject optional
          </AttenzioButton>
          <AttenzioButton variant="primary" size="sm" onClick={() => choose("accepted")}>
            Accept
          </AttenzioButton>
        </div>
      </div>
    </div>
  );
}
